import React, { Props, useState, useEffect } from 'react';

import { AnyObject, FORM_ERROR } from 'final-form';
import { FormOptions } from '@data-driven-forms/react-form-renderer/dist/cjs/renderer-context';
import { Row, Col, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import FormTemplateRenderProps from '@data-driven-forms/react-form-renderer/dist/cjs/form-template-render-props';

import { AppRootState, navigateToServiceRequest } from '../redux';
import { API_DEMO_APPLICATION_NAME, Physician, useCreatePhysician } from '../api';
import { HcpButton, HcpFormRenderer, HcpFormTemplate, PrivateLayout } from '../components';
import { ROUTE_PATHS, getAddPrescriberSchema, FIELD_NAMES, getConfigurationDataKey } from '../utils';
import { AnyType } from '../interfaces';
import { standardPortalConfig } from '../utils/constants';

const { prescriberFields } = FIELD_NAMES;

export interface AddPrescriberViewProps extends Props<JSX.Element> {
  isStartRequestPage?: boolean;
  patientId?: string;
  addedNewPrescriber: (prescriberId: number | null | undefined) => void;
  setShowModal: (value: boolean) => void;
  setLoading: (value: boolean) => void;
}
export const AddPrescriberView = (props: AddPrescriberViewProps) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: AppRootState) => state.auth.session?.user);
  const [formKey, setFormKey] = useState(new Date().toISOString());
  const { mutate: createPhysician } = useCreatePhysician({});
  const portal_ApplicationName = useSelector((state: AppRootState) => state.app?.config?.applicationName);
  const onSubmit = async (values: AnyObject) => {
    try {
      props.setLoading(true);
      const payload: Physician = {
        firstName: values[prescriberFields.firstName],
        lastName: values[prescriberFields.lastName],
        licenseState: values[prescriberFields.licensingState],
        middleName: values[prescriberFields.middleName],
        npi: values[prescriberFields.npi] && values[prescriberFields.npi]?.toString(),
        userName: values[prescriberFields.userName],
        userRole: (values?.is_admin as AnyType[])?.[0] ? 'Admin' : null,
        applicationName: standardPortalConfig.isStandardPortalUrl
          ? String(API_DEMO_APPLICATION_NAME)
          : String(portal_ApplicationName),
        stateLicenseNumber: values[prescriberFields.stateLicenseNumber],
        taxId: values[prescriberFields.taxId],
        ptan: values[prescriberFields.ptan],
        dea: values[prescriberFields.dea],
        email: values[prescriberFields.email],
        hin: values[prescriberFields.hin]
      };

      const result = await createPhysician(payload);

      if (result.errors && result.errors.length > 0) {
        const errors = result.errors?.map((e) => e.description).join(' ');

        // Return the form error to display
        return { [FORM_ERROR]: errors };
      }

      // Submission worked so let's redirect, if desired based on the button used
      if (values.redirect === true) {
        if (props.isStartRequestPage) {
          props.addedNewPrescriber(result?.physicianId);
          return;
        }
        //dispatch(navigateToPracticePrescribers());
        props.setShowModal(false);
        return;
      }

      // Low-tech way to reset the form until form.restart is available;
      // see https://github.com/final-form/react-final-form/issues/840
      setFormKey(new Date().toISOString());
    } catch (e: any) {
      return { [FORM_ERROR]: e.message };
    } finally {
      props.setLoading(false);
    }
  };

  const backNavigationText = useSelector(
    (state: AppRootState) =>
      (((state.app?.entities?.resources as AnyType) ?? {})['back.navigation.text' ?? ''] as AnyType)?.data
  );

  if (props.isStartRequestPage) {
    return (
      <HcpFormRenderer
        contentKey={getConfigurationDataKey('form.prescriber')}
        FormTemplate={(props) => <AddPrescriberFormTemplate {...props} isStartRequestPage={true} />}
        key={formKey}
        onCancel={() => props.setShowModal(false)}
        onSubmit={onSubmit}
        noErrorMessage={true}
        schemaMethod={getAddPrescriberSchema}
      />
    );
  }

  return (
    <HcpFormRenderer
      contentKey={getConfigurationDataKey('form.prescriber')}
      FormTemplate={AddPrescriberFormTemplate}
      key={formKey}
      onCancel={() => props.setShowModal(false)}
      onSubmit={onSubmit}
      schemaMethod={getAddPrescriberSchema}
      noErrorMessage={true}
    />
  );
};

export interface AddPrescriberFormTemplateProps extends FormTemplateRenderProps {
  isStartRequestPage?: boolean;
}

export const AddPrescriberFormTemplate = (props: AddPrescriberFormTemplateProps) => {
  const submitForm = (formProps: AnyObject) => {
    formProps.change('redirect', true);
    formProps.submit();
  };

  return (
    <HcpFormTemplate
      {...props}
      renderControls={(formProps: FormOptions) => {
        const { submitting, hasValidationErrors, submitError, values } = formProps.getState();
        return (
          <>
            {submitError && <p className='text-danger'>{submitError}</p>}
            <Row>
              {!props.isStartRequestPage && (
                <Col md={8} className='d-flex flex-column flex-lg-row justify-content-md-start  gap-3'>
                  <HcpButton
                    className='mb-2 mb-lg-0 mr-lg-2 px-4 text-nowrap'
                    disabled={submitting || hasValidationErrors}
                    onClick={() => formProps.change('redirect', true)}
                    type='submit'
                    variant='secondary'
                    data-testid='cmx__add-prescriber-button'
                    loading={submitting && values.redirect}>
                    {submitting && values.redirect ? 'SUBMITTING...' : 'Add Prescriber and Close'}
                  </HcpButton>
                  <HcpButton
                    disabled={submitting || hasValidationErrors}
                    className='mt-2 mt-lg-0 ml-lg-2 px-4 text-nowrap'
                    onClick={() => formProps.change('redirect', false)}
                    type='submit'
                    variant='outline-secondary'
                    loading={submitting && !values.redirect}>
                    {submitting && !values.redirect ? 'SUBMITTING...' : 'Save Prescriber and Add Another One'}
                  </HcpButton>
                </Col>
              )}

              {props.isStartRequestPage && (
                <Col md={8} className='d-flex flex-column flex-lg-row justify-content-md-start'>
                  <HcpButton
                    className='mb-2 mb-lg-0 mr-lg-2'
                    disabled={submitting || hasValidationErrors}
                    onClick={() => submitForm(formProps)}
                    data-testid='cmx__add-prescriber-button'
                    variant='secondary'
                    loading={submitting && values.redirect}>
                    {submitting && values.redirect ? 'SUBMITTING...' : 'Add Prescriber and Select'}
                  </HcpButton>
                </Col>
              )}

              <Col xs={12} md={4} className='d-flex justify-content-md-end gap-3'>
                <HcpButton variant='outline-danger' className='mt-3 mt-md-0' onClick={formProps.onCancel}>
                  Cancel
                </HcpButton>
              </Col>
            </Row>
          </>
        );
      }}
    />
  );
};
